import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Target, Zap, Mail, ChevronRight } from "lucide-react";
import { CampaignStatusBadge } from "./campaignModel";

const Stat = ({ value, label, color = "text-white" }) => (
  <div className="text-center">
    <p className={`${color} font-semibold`}>{value}</p>
    <p className="text-xs text-gray-500">{label}</p>
  </div>
);

const CampaignCard = ({ campaign: c, onOpen }) => (
  <motion.div
    className="glass-card p-5 cursor-pointer hover:border-ism-teal/30 transition-colors group"
    onClick={() => onOpen(c.campaignId)}
    whileHover={{ scale: 1.003 }}
    data-testid={`campaign-card-${c.campaignId}`}
  >
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-4 min-w-0">
        <div className="p-2.5 rounded-xl bg-ism-teal/10 border border-ism-teal/20 flex-shrink-0">
          <Mail className="h-5 w-5 text-ism-teal" />
        </div>
        <div className="min-w-0">
          <h3 className="text-white font-medium flex items-center gap-2 flex-wrap">
            {c.name}
            <CampaignStatusBadge status={c.status} />
          </h3>
          <p className="text-xs text-gray-500 mt-0.5 truncate">
            {c.sourceFile} — {new Date(c.createdAt).toLocaleDateString()}
            {c.cadenceConfig?.steps && ` — ${c.cadenceConfig.steps} steps`}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-6 text-sm flex-shrink-0">
        <Stat value={c.totalProspects} label="Prospects" />
        <Stat value={c.enrichedCount || 0} label="Enriched" color="text-ism-teal" />
        <Stat value={c.sentCount || 0} label="Sent" color="text-amber-400" />
        <Stat value={c.repliedCount || 0} label="Replied" color="text-green-400" />
        <ChevronRight className="h-5 w-5 text-gray-600 group-hover:text-ism-teal transition-colors" />
      </div>
    </div>
  </motion.div>
);

const EmptyState = ({ onCreate }) => (
  <div className="glass-card p-12 text-center">
    <div className="inline-flex p-4 rounded-2xl bg-ism-teal/10 border border-ism-teal/20 mb-4">
      <Target className="h-10 w-10 text-ism-teal" />
    </div>
    <h3 className="text-lg text-white mb-2">No Campaigns Yet</h3>
    <p className="text-sm text-gray-400 mb-6 max-w-md mx-auto">
      Upload a CSV of prospects or pick from your CRM leads. ISM will enrich contacts,
      craft personalized outreach emails, and prepare a multi-step cadence for your approval.
    </p>
    <Button onClick={onCreate} className="bg-ism-teal hover:bg-ism-teal/90 text-black font-medium" data-testid="upload-first-campaign">
      <Zap className="h-4 w-4 mr-2" /> Create Your First Campaign
    </Button>
  </div>
);

export const CampaignList = ({ campaigns, loading, onOpen, onCreate }) => (
  <div className="space-y-6" data-testid="campaigns-view">
    <div className="flex items-center justify-between gap-4">
      <div>
        <h1 className="text-2xl text-white font-semibold">Outreach Campaigns</h1>
        <p className="text-sm text-gray-400 mt-1">Upload prospects or select from CRM — AI crafts personalized cadences — you approve and send</p>
      </div>
      <Button onClick={onCreate} className="bg-ism-teal hover:bg-ism-teal/90 text-black font-medium flex-shrink-0" data-testid="new-campaign-btn">
        <Plus className="h-4 w-4 mr-2" /> New Campaign
      </Button>
    </div>

    {loading ? (
      <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-ism-teal" /></div>
    ) : campaigns.length === 0 ? (
      <EmptyState onCreate={onCreate} />
    ) : (
      <div className="grid gap-4">
        {campaigns.map((c) => <CampaignCard key={c.campaignId} campaign={c} onOpen={onOpen} />)}
      </div>
    )}
  </div>
);
